import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import dayjs from 'dayjs';

import { CustomTooltip } from 'components/InfoTooltip';

import { TranscriptReactions } from './constants';

// Who each bubble belongs to, shown above it.
const Speakers = {
  interviewer: 'Interviewer',
  candidate: 'Your Response'
};

const SpeakerStyle = {
  fontSize: '11px',
  lineHeight: '11px',
  color: 'neutral.clr-500'
};

const INTERVIEWER_BUBBLE_COLOR = '#FFFFFF';
const CANDIDATE_BUBBLE_COLOR = '#D9FDD3';
const FILLER_COLOR = '#FEF08A';
const TRANSCRIPT_COLOR = '#F0F2F5';

const ALL_REACTIONS = 'ALL';

// Chip palettes, keyed by the tone each reaction carries.
const ReactionTones = {
  positive: { backgroundColor: 'success.clr-100', color: 'success.clr-700' },
  improve: { backgroundColor: 'warning.clr-100', color: 'warning.clr-500' },
  negative: { backgroundColor: 'danger.clr-100', color: 'danger.clr-700' },
  neutral: { backgroundColor: 'neutral.clr-100', color: 'neutral.clr-700' }
};

const IdleFilterStyle = {
  backgroundColor: 'shades.clr-white-900',
  borderColor: 'neutral.clr-200',
  color: 'neutral.clr-600'
};

const ActiveFilterStyle = {
  backgroundColor: 'primary.clr-400',
  borderColor: 'primary.clr-400',
  color: 'shades.clr-white-900'
};

// Filler features the speech analysis reports per answer, each carrying the
// words it matched.
const FillerSubCategories = ['FILLER_WORDS', 'FILLER_PHRASES', 'FILLER_UTTERANCES'];

const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    boxShadow: `0px 4px 4px 0px #CCD4DE1A,
    0px 10px 20px 0px #CCD4DE29,
    0px 0px 4px 0px #CCD4DE`
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(4),
    height: '64px',
    flexShrink: 0,
    padding: theme.spacing(0, 4),
    backgroundColor: theme.palette.primary['clr-400']
  },
  transcript: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    height: '376px',
    padding: theme.spacing(5),
    overflowY: 'auto',
    backgroundColor: TRANSCRIPT_COLOR,
    '&::-webkit-scrollbar': { width: '10px' },
    '&::-webkit-scrollbar-track': { backgroundColor: theme.palette.shades['clr-grey-100'] },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.neutral['clr-300'],
      borderRadius: '5px'
    }
  },
  message: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    maxWidth: '44%'
  },
  interviewerMessage: {
    alignSelf: 'flex-start',
    alignItems: 'flex-start'
  },
  candidateMessage: {
    alignSelf: 'flex-end',
    alignItems: 'flex-end'
  },
  bubble: {
    width: '100%',
    borderRadius: '2px',
    padding: theme.spacing(2.5, 3)
  },
  reactions: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: theme.spacing(1.5)
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexShrink: 0,
    padding: theme.spacing(3, 5, 0),
    backgroundColor: TRANSCRIPT_COLOR
  },
  chip: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    height: '22px',
    borderRadius: '11px',
    padding: theme.spacing(0, 2.5),
    border: '1px solid transparent'
  },
  filterChip: {
    cursor: 'pointer',
    userSelect: 'none'
  },
  filler: {
    backgroundColor: FILLER_COLOR,
    color: 'inherit',
    borderRadius: '2px',
    padding: '1px 4px'
  },
}));

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// The interviewer's turns are stored as the raw JSON the model returned.
const parseInterviewerMessage = (content) => {
  try {
    return JSON.parse(content)?.message || content;
  } catch (error) {
    return content;
  }
};

// Speech analysis reports the words it flagged, but its character offsets are
// measured on a punctuation-stripped copy of the transcript, so the words are
// matched against the stored answer instead.
const getFillerWords = (analysis) => {
  if (!analysis || analysis.isError) return [];

  return (analysis?.data?.communication?.features || [])
    .filter(({ subCategory }) => FillerSubCategories.includes(subCategory))
    .reduce((words, { value }) => (
      Array.isArray(value) ? [...words, ...value.map((filler) => filler?.word)] : words
    ), [])
    .filter(Boolean);
};

// Splits an answer into plain text and the filler words to highlight.
const toSegments = (text = '', fillers = []) => {
  const patterns = fillers.filter(Boolean);

  if (!patterns.length) return [{ text }];

  // Longest first, so "you know" wins over "know".
  const matcher = new RegExp(
    `\\b(${[...patterns].sort((a, b) => b.length - a.length).map(escapeRegExp).join('|')})\\b`,
    'gi'
  );

  const segments = [];
  let lastIndex = 0;
  let match = matcher.exec(text);

  while (match !== null) {
    if (match.index > lastIndex) segments.push({ text: text.slice(lastIndex, match.index) });

    segments.push({ text: match[0], filler: true });

    lastIndex = matcher.lastIndex;
    match = matcher.exec(text);
  }

  if (lastIndex < text.length) segments.push({ text: text.slice(lastIndex) });

  return segments;
};

// Reactions the evaluation sent, resolved to the tags this UI knows about and
// paired with their notes. Entries are `{ code, note }`; a bare code string is
// accepted too, so a turn stored before notes existed still renders.
const toReactions = (entries) => (Array.isArray(entries) ? entries : [])
  .map((entry) => {
    const code = typeof entry === 'string' ? entry : entry?.code;
    const tag = TranscriptReactions.find((reaction) => reaction.id === code);

    if (!tag) return null;

    return { ...tag, note: typeof entry === 'string' ? '' : (entry?.note || '') };
  })
  .filter(Boolean);

const formatDuration = (startedAt, finishedAt) => {
  if (!startedAt || !finishedAt) return '';

  const total = Math.max(dayjs(finishedAt).diff(dayjs(startedAt), 'second'), 0);
  const parts = [Math.floor(total / 3600), Math.floor((total % 3600) / 60), total % 60];

  return parts.map((part) => String(part).padStart(2, '0')).join(':');
};

const MessageText = ({ segments, sx }) => {
  const classes = useStyles();

  return (
    <Typography
      variant='body02-medium'
      color='primary.clr-400'
      sx={{ lineHeight: '20px', ...sx }}
    >
      {segments.map(({ text, filler }, index) => (
        filler
          ? <Box key={index} component='span' className={classes.filler}>{text}</Box>
          : <React.Fragment key={index}>{text}</React.Fragment>
      ))}
    </Typography>
  );
};

const CaseStudy = ({ caseMaster, followUps, analysis = [], startedAt, finishedAt }) => {
  const classes = useStyles();

  const [activeReaction, setActiveReaction] = React.useState(ALL_REACTIONS);

  const duration = formatDuration(startedAt, finishedAt);

  const messages = React.useMemo(() => {
    const conversation = [];

    if (caseMaster?.problem) {
      conversation.push({ role: 'interviewer', content: caseMaster.problem });
    }

    let answerIndex = 0;

    (Array.isArray(followUps) ? followUps : []).forEach((entry) => {
      if (entry?.role !== 'user') {
        conversation.push({ role: 'interviewer', content: parseInterviewerMessage(entry?.content) });
        return;
      }

      // One analysis is stored per answer, in the same order as the answers.
      conversation.push({
        role: 'candidate',
        content: entry.content || '',
        fillers: getFillerWords(analysis?.[answerIndex]),
        // Reaction tags for this answer, written when the answer was given.
        // Attempts taken before that shipped carry none and render without.
        reactions: toReactions(entry.reactions)
      });

      answerIndex += 1;
    });

    return conversation;
  }, [caseMaster, followUps, analysis]);

  // Only reactions that actually occur get a filter, each carrying its count.
  const filters = React.useMemo(() => {
    const counts = messages.reduce((tally, { reactions = [] }) => {
      reactions.forEach(({ id }) => { tally[id] = (tally[id] || 0) + 1; });
      return tally;
    }, {});

    return TranscriptReactions
      .filter(({ id }) => counts[id])
      .map((reaction) => ({ ...reaction, count: counts[reaction.id] }));
  }, [messages]);

  // Filtering keeps the question that prompted each answer, so a filtered
  // transcript still reads as a conversation.
  const visibleMessages = React.useMemo(() => {
    if (activeReaction === ALL_REACTIONS) return messages;

    return messages.filter((message, index) => {
      const matches = ({ reactions = [] }) => reactions.some(({ id }) => id === activeReaction);
      const next = messages[index + 1];

      return message.role === 'candidate'
        ? matches(message)
        : Boolean(next && next.role === 'candidate' && matches(next));
    });
  }, [messages, activeReaction]);

  return (
    <Box className={classes.card}>
      <Box className={classes.header}>
        <Typography
          variant='body02-bold'
          color='shades.clr-white-900'
          sx={{ fontSize: '13px' }}
        >
          Session Transcript
        </Typography>

        {duration && (
          <Typography variant='body02-regular' color='primary.clr-100' sx={{ fontSize: '12px' }}>
            Total duration: {duration}
          </Typography>
        )}
      </Box>

      {!!filters.length && (
        <Box className={classes.filters}>
          {[{ id: ALL_REACTIONS, label: 'All', count: messages.filter(({ role }) => role === 'candidate').length }, ...filters]
            .map(({ id, label, emoji, count }) => {
              const active = id === activeReaction;

              return (
                <Box
                  key={id}
                  className={`${classes.chip} ${classes.filterChip}`}
                  sx={active ? ActiveFilterStyle : IdleFilterStyle}
                  onClick={() => setActiveReaction(id)}
                >
                  {emoji && <span>{emoji}</span>}
                  <Typography variant='body02-semiBold' sx={{ fontSize: '11px' }}>
                    {label} {count}
                  </Typography>
                </Box>
              );
            })}
        </Box>
      )}

      <Box className={classes.transcript}>
        {visibleMessages.map((message, index) => (
          message.role === 'interviewer' ? (
            <Box key={index} className={`${classes.message} ${classes.interviewerMessage}`}>
              <Typography variant='body02-semiBold' sx={SpeakerStyle}>
                {Speakers.interviewer}
              </Typography>

              <Box className={classes.bubble} sx={{ backgroundColor: INTERVIEWER_BUBBLE_COLOR }}>
                <MessageText segments={[{ text: message.content }]} />
              </Box>
            </Box>
          ) : (
            <Box key={index} className={`${classes.message} ${classes.candidateMessage}`}>
              <Typography variant='body02-semiBold' sx={SpeakerStyle}>
                {Speakers.candidate}
              </Typography>

              <Box className={classes.bubble} sx={{ backgroundColor: CANDIDATE_BUBBLE_COLOR }}>
                <MessageText segments={toSegments(message.content, message.fillers)} />
              </Box>

              {!!message.reactions.length && (
                <Box className={classes.reactions}>
                  {message.reactions.map(({ id, label, emoji, tone, note }) => (
                    // The note is why the reaction was given; on the chip
                    // itself it would crowd the transcript, so it is on hover.
                    <CustomTooltip key={id} component={note || label}>
                      <Box className={classes.chip} sx={ReactionTones[tone]}>
                        <span>{emoji}</span>
                        <Typography variant='body02-semiBold' sx={{ fontSize: '11px' }}>
                          {label}
                        </Typography>
                      </Box>
                    </CustomTooltip>
                  ))}
                </Box>
              )}
            </Box>
          )
        ))}
      </Box>
    </Box>
  );
};

export default CaseStudy;
